import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, X, Plus, Play, Radar } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const PROPERTY_TYPES = [
  "Vacant Land", "Residential Infill Lot", "Teardown", "Oversized Residential Lot",
  "Subdividable Parcel", "Entitlement Opportunity", "Small Residential Development",
  "Existing Structure With Redevelopment Potential",
];
const STRATEGIES = [
  "Single-Family Spec Home", "Small-Lot Development", "Lot Split", "Subdivision",
  "Entitlement Flip", "SB 9", "SB 1123", "Duplex", "Multifamily", "ADU Opportunity",
  "Build and Sell", "Build and Hold",
];
const SITE_CHARS = [
  "Flat", "Gentle Slope", "Moderate Slope", "Hillside", "Corner Lot",
  "Multiple Street Frontages", "Paved Road", "Legal Access", "Lake View",
  "Mountain View", "Ocean View",
];
const EXCLUSIONS = [
  "Landlocked", "No Legal Access", "Extreme Hillside", "Floodway",
  "Major Environmental Constraints", "Manufactured / Mobile Home Only",
  "Commercial Only", "Industrial Only", "Excessive Retaining Wall Requirements",
  "Unusable Parcel Shape", "No Development Upside",
];
const WATER = ["Public Water Required", "Public Water Preferred", "Well Acceptable", "Unknown Acceptable"];
const SEWER = ["Public Sewer Required", "Public Sewer Preferred", "Septic Acceptable", "Unknown Acceptable"];
const ELECTRIC = ["At Property", "At Street", "Nearby", "Unknown Acceptable"];
const TIMEZONES = [
  { v: "America/Los_Angeles", label: "Pacific (PT)" },
  { v: "America/Denver", label: "Mountain (MT)" },
  { v: "America/Phoenix", label: "Arizona (MST)" },
  { v: "America/Chicago", label: "Central (CT)" },
  { v: "America/New_York", label: "Eastern (ET)" },
];
const WD = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

const num = (v) => (v === "" || v == null ? null : Number(v));

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} />
        <Button type="button" variant="outline" size="icon" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ChipSelect({ options, value = [], onChange, allowCustom, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  const custom = value.filter((v) => !options.includes(v));
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" data-testid={testid ? `${testid}-${o}` : undefined} onClick={() => toggle(o)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              value.includes(o) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-input"}`}>
            {o}
          </button>
        ))}
        {custom.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)}
            className="text-xs px-3 py-1.5 rounded-full border bg-primary text-primary-foreground border-primary inline-flex items-center gap-1">
            {o} <X className="h-3 w-3" />
          </button>
        ))}
      </div>
      {allowCustom && (
        <div className="mt-2">
          <TagInput value={custom} onChange={(c) => onChange([...value.filter((v) => options.includes(v)), ...c])}
            placeholder="Add custom…" testid={testid ? `${testid}-custom` : undefined} />
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function AIBuyBoxEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [f, setF] = useState({
    name: "", description: "", status: "draft",
    schedule: { enabled: false, frequency: "daily", weekdays: [], time: "05:00", timezone: "America/Los_Angeles" },
    leads_per_run: 8,
    deal_channel: "off_market",
    geography: { country: "USA", state: "", counties: [], cities: [], zips: [], radius: "" },
    property_types: [], development_strategies: [],
    criteria: {},
    zoning: { preferred: [], acceptable: [], avoid: [], residential_only: false, rezoning_considered: false, gpa_considered: false, notes: "" },
    utilities: { water: "", sewer: "", electric: "", gas: "", internet: "", notes: "" },
    site: { characteristics: [], max_slope: "", no_hoa_preferred: false },
    exclusions: [],
    financials: {},
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/ai-buybox/${id}`);
        setF((prev) => ({ ...prev, ...data,
          schedule: { ...prev.schedule, ...(data.schedule || {}) },
          geography: { ...prev.geography, ...(data.geography || {}) },
          criteria: data.criteria || {}, zoning: { ...prev.zoning, ...(data.zoning || {}) },
          utilities: { ...prev.utilities, ...(data.utilities || {}) }, site: { ...prev.site, ...(data.site || {}) },
          financials: { ...prev.financials, ...(data.financials || {}) } }));
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNested = (group, k, v) => setF((p) => ({ ...p, [group]: { ...p[group], [k]: v } }));
  const toggleWeekday = (d) => {
    const cur = f.schedule.weekdays || [];
    setNested("schedule", "weekdays", cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("AI Buy Box name is required"); return null; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/ai-buybox/${id}`, f);
        toast.success("AI Buy Box saved");
        return id;
      }
      const { data } = await api.post("/ai-buybox", f);
      toast.success("AI Buy Box created");
      nav(`/ai-buybox/${data.id}/edit`, { replace: true });
      return data.id;
    } catch (e) { toast.error(apiError(e)); return null; }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    const savedId = await save();
    if (!savedId) return;
    setRunning(true);
    try {
      await api.post(`/ai-buybox/${savedId}/run`);
      toast.success("Sourcing land from your data providers — check your Leads list shortly");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  }

  const NumField = ({ group, k, label, hint }) => (
    <Field label={label} hint={hint}>
      <Input type="number" data-testid={`aibb-${group}-${k}`} value={f[group]?.[k] ?? ""}
        onChange={(e) => setNested(group, k, num(e.target.value))} />
    </Field>
  );

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto pb-24" data-testid="ai-buy-box-editor">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => nav("/ai-buybox")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-0.5"><Radar className="h-3.5 w-3.5" /> AI Buy Box</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{isEdit ? "Edit AI Buy Box" : "New AI Buy Box"}</h1>
        </div>
        {isEdit && (
          <Button variant="outline" size="sm" data-testid="run-now-btn" onClick={runNow} disabled={running || saving}>
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />} Run Now
          </Button>
        )}
        <Button data-testid="save-ai-buy-box-btn" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["identity", "schedule", "geo", "types"]} className="space-y-3">
        <Section value="identity" title="1. Identity">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="AI Buy Box Name">
              <Input data-testid="aibb-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="SoCal Infill Land Hunt" />
            </Field>
            <Field label="Status" hint="Only Active boxes run on schedule">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="aibb-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Mandate / Notes for Angela" hint="Plain-English guidance to steer the search">
                <Textarea data-testid="aibb-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={2}
                  placeholder="Focus on teardowns and splittable lots within 20 miles of Temecula that pencil for spec homes." />
              </Field>
            </div>
          </div>
        </Section>

        <Section value="schedule" title="2. Schedule — When Angela Hunts">
          <div className="mb-4">
            <Label className="text-sm">Deal Channel — which market to source from</Label>
            <div className="mt-2 inline-flex rounded-lg border border-border p-1 bg-secondary/30" data-testid="aibb-deal-channel">
              <button
                type="button"
                data-testid="aibb-channel-off_market"
                onClick={() => set("deal_channel", "off_market")}
                className={`rounded-md px-4 py-1.5 text-sm transition-colors ${(f.deal_channel || "off_market") === "off_market" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Off-Market · PropertyRadar
              </button>
              <button
                type="button"
                data-testid="aibb-channel-on_market"
                onClick={() => set("deal_channel", "on_market")}
                className={`rounded-md px-4 py-1.5 text-sm transition-colors ${f.deal_channel === "on_market" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                On-Market · RealEstateAPI
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Off-Market pulls owner + parcel intel from PropertyRadar. On-Market pulls active MLS land listings from RealEstateAPI. The field mapping from the matching channel is applied automatically when leads are created.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2.5 mb-4">
            <Switch data-testid="aibb-schedule-enabled" checked={!!f.schedule.enabled} onCheckedChange={(v) => setNested("schedule", "enabled", v)} />
            <span className="text-sm font-medium">Run automatically on a schedule</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Frequency">
              <Select value={f.schedule.frequency} onValueChange={(v) => setNested("schedule", "frequency", v)}>
                <SelectTrigger data-testid="aibb-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Leads per run" hint="Angela returns her best finds (max 15)">
              <Input type="number" min={1} max={15} data-testid="aibb-leads-per-run" value={f.leads_per_run}
                onChange={(e) => set("leads_per_run", Math.max(1, Math.min(15, Number(e.target.value) || 1)))} />
            </Field>
            <Field label="Run time">
              <Input type="time" data-testid="aibb-time" value={f.schedule.time} onChange={(e) => setNested("schedule", "time", e.target.value)} />
            </Field>
            <Field label="Timezone">
              <Select value={f.schedule.timezone} onValueChange={(v) => setNested("schedule", "timezone", v)}>
                <SelectTrigger data-testid="aibb-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {f.schedule.frequency === "weekly" && (
            <div className="mt-4">
              <Label className="text-sm">Days of week</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {WD.map(([lbl, d]) => (
                  <button key={d} type="button" data-testid={`aibb-weekday-${d}`} onClick={() => toggleWeekday(d)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      (f.schedule.weekdays || []).includes(d) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-input"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            New matching leads land in your Leads list and are auto-enriched by the agents. A summary of each run
            appears on the AI Buy Box card. Scheduled runs happen while the app is deployed and running.
          </p>
        </Section>

        <Section value="geo" title="3. Geography">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Country"><Input data-testid="aibb-country" value={f.geography.country} onChange={(e) => setNested("geography", "country", e.target.value)} /></Field>
            <Field label="State"><Input data-testid="aibb-state" value={f.geography.state} onChange={(e) => setNested("geography", "state", e.target.value)} placeholder="CA" /></Field>
            <Field label="Optional radius / target area"><Input value={f.geography.radius} onChange={(e) => setNested("geography", "radius", e.target.value)} placeholder="e.g. 30 miles from Menifee" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 mt-4">
            <Field label="Counties"><TagInput testid="aibb-counties" value={f.geography.counties} onChange={(v) => setNested("geography", "counties", v)} placeholder="Add county" /></Field>
            <Field label="Cities"><TagInput testid="aibb-cities" value={f.geography.cities} onChange={(v) => setNested("geography", "cities", v)} placeholder="Add city" /></Field>
            <Field label="ZIP Codes"><TagInput testid="aibb-zips" value={f.geography.zips} onChange={(v) => setNested("geography", "zips", v)} placeholder="Add ZIP" /></Field>
          </div>
        </Section>

        <Section value="types" title="4. Property Types">
          <ChipSelect testid="aibb-property-types" options={PROPERTY_TYPES} value={f.property_types} onChange={(v) => set("property_types", v)} allowCustom />
        </Section>

        <Section value="strat" title="5. Development Strategies">
          <ChipSelect testid="aibb-strategies" options={STRATEGIES} value={f.development_strategies} onChange={(v) => set("development_strategies", v)} allowCustom />
        </Section>

        <Section value="criteria" title="6. Acquisition Criteria">
          <div className="grid gap-4 sm:grid-cols-3">
            <NumField group="criteria" k="min_price" label="Min Purchase Price" />
            <NumField group="criteria" k="max_price" label="Max Purchase Price" />
            <div />
            <NumField group="criteria" k="min_lot_sf" label="Min Lot SF" />
            <NumField group="criteria" k="max_lot_sf" label="Max Lot SF" />
            <div />
            <NumField group="criteria" k="min_acreage" label="Min Acreage" />
            <NumField group="criteria" k="max_acreage" label="Max Acreage" />
            <div />
            <NumField group="criteria" k="min_lots" label="Min Potential Lots" />
            <NumField group="criteria" k="max_lots" label="Max Potential Lots" />
          </div>
        </Section>

        <Section value="zoning" title="7. Zoning Criteria">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Preferred Zoning"><TagInput value={f.zoning.preferred} onChange={(v) => setNested("zoning", "preferred", v)} placeholder="R-1, R-2…" /></Field>
            <Field label="Acceptable Zoning"><TagInput value={f.zoning.acceptable} onChange={(v) => setNested("zoning", "acceptable", v)} placeholder="Add code" /></Field>
            <Field label="Zoning To Avoid"><TagInput value={f.zoning.avoid} onChange={(v) => setNested("zoning", "avoid", v)} placeholder="Add code" /></Field>
          </div>
          <div className="flex flex-wrap gap-6 mt-4">
            <SwitchRow label="Residential Only" checked={f.zoning.residential_only} onChange={(v) => setNested("zoning", "residential_only", v)} />
            <SwitchRow label="Rezoning Considered" checked={f.zoning.rezoning_considered} onChange={(v) => setNested("zoning", "rezoning_considered", v)} />
            <SwitchRow label="General Plan Amendment Considered" checked={f.zoning.gpa_considered} onChange={(v) => setNested("zoning", "gpa_considered", v)} />
          </div>
          <div className="mt-4"><Field label="Zoning Notes"><Textarea value={f.zoning.notes} onChange={(e) => setNested("zoning", "notes", e.target.value)} rows={2} /></Field></div>
        </Section>

        <Section value="util" title="8. Utilities">
          <div className="grid gap-4 sm:grid-cols-3">
            <UtilSelect label="Water" options={WATER} value={f.utilities.water} onChange={(v) => setNested("utilities", "water", v)} />
            <UtilSelect label="Sewer" options={SEWER} value={f.utilities.sewer} onChange={(v) => setNested("utilities", "sewer", v)} />
            <UtilSelect label="Electric" options={ELECTRIC} value={f.utilities.electric} onChange={(v) => setNested("utilities", "electric", v)} />
          </div>
        </Section>

        <Section value="site" title="9. Site Characteristics">
          <ChipSelect options={SITE_CHARS} value={f.site.characteristics} onChange={(v) => setNested("site", "characteristics", v)} allowCustom />
          <div className="grid gap-4 sm:grid-cols-2 mt-4">
            <Field label="Maximum Acceptable Slope (%)"><Input type="number" value={f.site.max_slope} onChange={(e) => setNested("site", "max_slope", e.target.value)} /></Field>
            <div className="flex items-end"><SwitchRow label="No HOA Preferred" checked={f.site.no_hoa_preferred} onChange={(v) => setNested("site", "no_hoa_preferred", v)} /></div>
          </div>
        </Section>

        <Section value="excl" title="10. Deal Breakers / Exclusions">
          <ChipSelect testid="aibb-exclusions" options={EXCLUSIONS} value={f.exclusions} onChange={(v) => set("exclusions", v)} allowCustom />
        </Section>
      </Accordion>
    </div>
  );
}

const Section = ({ value, title, children }) => (
  <AccordionItem value={value} className="border rounded-lg px-4 bg-card">
    <AccordionTrigger className="text-base font-medium hover:no-underline" data-testid={`aibb-section-${value}`}>{title}</AccordionTrigger>
    <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
  </AccordionItem>
);

const SwitchRow = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <Switch checked={!!checked} onCheckedChange={onChange} />
    <span className="text-sm">{label}</span>
  </label>
);

const UtilSelect = ({ label, options, value, onChange }) => (
  <Field label={label}>
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Any / not specified</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);
